import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";

const GROUP_FILL = {
  priority: "hsl(var(--accent-highlight))",
  watch: "hsl(var(--accent-positive))",
  neutral: "hsl(var(--foreground))",
};

export const NetworkGraph = ({ graph, selectedTicker, onSelectSymbol }) => {
  const nodes = graph?.nodes || [];
  const links = graph?.links || [];
  const [positions, setPositions] = useState(nodes);
  const rafRef = useRef(null);
  const tRef = useRef(0);

  useEffect(() => {
    setPositions(
      nodes.map((n) => ({ ...n, bx: n.x - 300, by: n.y - 210 })),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [graph]);

  useEffect(() => {
    if (!nodes.length) return undefined;
    const tick = () => {
      tRef.current += 0.012;
      setPositions((prev) =>
        prev.map((n) => ({
          ...n,
          x:
            300 +
            Math.cos((n.offset || 0) + tRef.current * (n.speed || 0.5)) *
              (n.ampX || 10) +
            (n.bx || 0),
          y:
            210 +
            Math.sin((n.offset || 0) + tRef.current * (n.speed || 0.5)) *
              (n.ampY || 10) +
            (n.by || 0),
        })),
      );
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [graph]);

  const nodeById = (id) => positions.find((p) => p.id === id);

  if (!nodes.length) {
    return (
      <div
        className="panel lg:col-span-7 flex flex-col min-h-[360px] p-4"
        data-testid="network-graph-panel"
      >
        <span className="label-uppercase text-magenta">Market Graph</span>
        <p className="mt-2 text-sm text-text-secondary font-mono">
          No scheduled_analysis rows to plot.
        </p>
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.7, delay: 0.15 }}
      className="panel lg:col-span-7 flex flex-col min-h-[360px]"
      data-testid="network-graph-panel"
    >
      <div className="panel-header">
        <div className="flex items-center gap-3">
          <h3 className="font-display text-sm font-bold tracking-tight uppercase">
            Market Relationship Graph
          </h3>
          <span className="label-micro">/ scheduled_analysis consensus map</span>
        </div>
        <div className="hidden sm:flex items-center gap-1.5">
          <span className="label-micro">{nodes.length} nodes</span>
          <span className="label-micro">·</span>
          <span className="label-micro">{links.length} edges</span>
        </div>
      </div>

      <div className="flex-1 grid grid-cols-12 min-h-0">
        <div className="col-span-12 sm:col-span-3 border-r border-border p-3 flex flex-col gap-3">
          <span className="label-micro">Legend</span>
          <LegendDot color="bg-magenta" label="PRIORITY" />
          <LegendDot color="bg-positive" label="WATCH" />
          <LegendDot color="bg-foreground" label="NEUTRAL" />

          <div className="mt-2 border-t border-border pt-3 flex flex-col gap-2">
            <span className="label-micro">Bias</span>
            <span
              className={`text-base font-mono ${
                graph.bias === "BEARISH" ? "text-magenta" : graph.bias === "BULLISH" ? "text-positive" : "text-foreground"
              }`}
            >
              {graph.bias}
            </span>
          </div>

          <div className="border-t border-border pt-3 flex flex-col gap-1">
            <span className="label-micro">Click node</span>
            <span className="text-[10px] text-text-secondary font-mono leading-snug">
              Select a stock to load its price levels in Live Spot Feed.
            </span>
          </div>
        </div>

        <div className="col-span-12 sm:col-span-6 relative">
          <svg viewBox="0 0 600 420" className="w-full h-full">
            <defs>
              <pattern id="ng-grid" width="30" height="30" patternUnits="userSpaceOnUse">
                <path d="M 30 0 L 0 0 0 30" fill="none" stroke="hsl(var(--chart-grid))" strokeWidth="0.5" />
              </pattern>
            </defs>
            <rect width="600" height="420" fill="url(#ng-grid)" />

            {links.map(([a, b], i) => {
              const na = nodeById(a);
              const nb = nodeById(b);
              if (!na || !nb) return null;
              return (
                <line
                  key={i}
                  x1={na.x}
                  y1={na.y}
                  x2={nb.x}
                  y2={nb.y}
                  stroke="hsl(var(--text-secondary))"
                  strokeWidth="0.6"
                  className="link-pulse"
                  style={{ animationDelay: `${(i % 6) * 0.4}s` }}
                />
              );
            })}

            {positions.map((n) => {
              const fill = GROUP_FILL[n.group] || GROUP_FILL.neutral;
              const isSelected = n.symbol === selectedTicker;
              return (
                <g
                  key={n.id}
                  onClick={() => onSelectSymbol?.(n.symbol)}
                  style={{ cursor: "pointer" }}
                  data-testid={`graph-node-${n.symbol}`}
                >
                  <circle cx={n.x} cy={n.y} r={n.size / 2} fill={fill} opacity="0.16" />
                  <circle cx={n.x} cy={n.y} r={n.size / 3.4} fill={fill} />
                  <circle
                    cx={n.x}
                    cy={n.y}
                    r={n.size / 2 + (isSelected ? 5 : 2)}
                    fill="none"
                    stroke={fill}
                    strokeWidth={isSelected ? 1.6 : 0.6}
                    opacity={isSelected ? 1 : 0.5}
                  />
                  <text
                    x={n.x}
                    y={n.y + n.size / 2 + 12}
                    textAnchor="middle"
                    fontSize="9"
                    fontFamily="JetBrains Mono"
                    fill={isSelected ? fill : "hsl(var(--text-secondary))"}
                    style={{ letterSpacing: "0.1em", textTransform: "uppercase" }}
                  >
                    {n.label}
                  </text>
                </g>
              );
            })}
          </svg>
        </div>

        <div className="col-span-12 sm:col-span-3 border-l border-border p-3 flex flex-col gap-3">
          <SideStat label="Top Pick" value={graph.topPick?.symbol || "—"} />
          <SideStat
            label="Top Score"
            value={graph.topPick?.score != null ? graph.topPick.score.toFixed(1) : "—"}
            tone="positive"
          />
          <SideStat label="Priority Names" value={String(graph.priorityCount ?? 0)} tone="magenta" />
          <SideStat
            label="Score Spread"
            value={graph.spread != null ? `${graph.spread.toFixed(1)}` : "—"}
          />
        </div>
      </div>
    </motion.div>
  );
};

const LegendDot = ({ color, label }) => (
  <div className="flex items-center gap-2">
    <span className={`w-2 h-2 ${color}`} style={{ borderRadius: 1 }} />
    <span className="text-[10px] uppercase tracking-[0.18em] text-foreground font-mono">
      {label}
    </span>
  </div>
);

const SideStat = ({ label, value, tone = "default" }) => {
  const toneClass =
    tone === "magenta" ? "text-magenta" : tone === "positive" ? "text-positive" : "text-foreground";
  return (
    <div className="flex flex-col gap-1">
      <span className="label-micro">{label}</span>
      <span className={`text-base font-mono num ${toneClass}`}>{value}</span>
    </div>
  );
};

export default NetworkGraph;
